import { DOCUMENT } from '@angular/common';
import { Component, ElementRef, Inject, Input, OnChanges, SimpleChanges, ViewChild, ViewContainerRef } from '@angular/core';
import { GlobalFunctions } from '@global/GlobalFunctions';
import { LevelAsset } from '@services/assets-data.service';
import { RuleEvent, OfflineEvent, DeviationEvent, EventsDataService, WebhookEvent } from '@services/events-data.service';
import { Subject, takeUntil, distinctUntilChanged, filter, finalize } from 'rxjs';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { MatMenu } from '@angular/material/menu';
import { Router } from '@angular/router';

@Component({
	selector: 'app-dashboard-events',
	templateUrl: './dashboard-events.component.html',
	styleUrls: ['./dashboard-events.component.scss']
})
export class DashboardEventsComponent implements OnChanges {
	/** accountGUID */
	@Input() accountGUID: string;

	/** organisationUID */
	@Input() organisationUID: string;

	/** panelData */
	@Input() panelData: any;

	/** startTime */
	@Input() startTime: number;

	/** endTime */
	@Input() endTime: number;

	/** creating */
	@Input() creating: boolean;

	/** titleMenu */
	@Input() titleMenu: MatMenu;

	/** assets */
	public assets: LevelAsset[] = [];

	/** ruleEvents */
	public ruleEvents: RuleEvent[] = [];

	/** filteredRuleEvents */
	public filteredRuleEvents: RuleEvent[] = [];

	/** sensorEvents */
	public sensorEvents: OfflineEvent[] = [];

	/** sensorEvents */
	public filteredSensorEvents: OfflineEvent[] = [];

	/** deviationEvents */
	public deviationEvents: DeviationEvent[] = [];

	/** deviationEvents */
	public filteredDeviationEvents: DeviationEvent[] = [];

	/** webhookEvents */
	public webhookEvents: WebhookEvent[] = [];

	/** filteredWebhookEvents */
	public filteredWebhookEvents: WebhookEvent[] = [];

	/** defaultDays */
	public defaultDays: number = 30;

	/** ruleEventFields */
	public ruleEventFields: Object = { text: "RuleName", value: "RuleEventID" };

	/** sensorEventFields */
	public sensorEventFields: Object = { text: "SensorName", value: "OfflineEventID" };

	/** deviationEventFields */
	public deviationEventFields: Object = { text: "AssetName", value: "DeviationEventID" };

	/** webhookEventFields */
	public webhookEventFields: Object = { text: "Description", value: "WebhookEventID" };

	/** listboxHeight */
	public listboxHeight: string = "100px";

	/** mainDiv */
	@ViewChild('main') mainDiv: ElementRef;

	/** mainDivOberserver */
	public mainDivOberserver: ResizeObserver;

	/** shortMonths */
	private shortMonths: string[] = [
		"Jan", "Feb", "Mar", "Apr", "May", "Jun",
		"Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
	];

	/**
	 * Default Constructor
	 * @param globalFunctions globalFunctions  
	 * @param eventsDataService eventsDataService 
	 * @param siteSubscriptions siteSubscriptions 
	 * @param document document 
	 * @param vcr vcr 
	 */
	constructor(
		public globalFunctions: GlobalFunctions,
		public eventsDataService: EventsDataService,
		public router: Router,
		private siteSubscriptions: siteSubscriptions,
		@Inject(DOCUMENT) public document: Document,
		private vcr: ViewContainerRef
	) {
	}

	/**
	 * ngOnChanges
	 * @param changes changes 
	 */
	ngOnChanges(changes: SimpleChanges): void {
		if (changes['startTime'] || changes['endTime']) {
			if (this.startTime && this.endTime) {
				this.getRuleEvents();
				this.getOfflineEvents();
				this.getDeviationEvents();
				this.getWebhookEvents();
			}
		}
	}

	/**
	 * ngOnInit
	 */
	ngOnInit(): void {
		if (this.panelData) {
			this.assets = this.panelData.assets;
			if (this.creating) {
				let endDate = new Date();
				let startDate = new Date();
				startDate.setDate(endDate.getDate() - this.defaultDays);
				this.startTime = startDate.getTime();
				this.endTime = endDate.getTime();
			}
		}
		this.getRuleEvents();
		// this.setUpObserver();
	}

	/**
	 * setUpObserver
	 */
	setUpObserver() {
		if (!this.mainDiv) {
			setTimeout(() => {
				this.setUpObserver()
			}, 100);
		} else {
			this.mainDivOberserver = new ResizeObserver(entries => {
				for (const entry of entries) {
					const element = entry.target;
					const titleElement = <HTMLElement>element.getElementsByClassName("title")[0];
					const titleHeight = titleElement.offsetHeight;
					this.listboxHeight = `${(element.clientHeight - titleHeight) * 0.8}px`;
				}
			});

			this.mainDivOberserver.observe(this.mainDiv.nativeElement);
		}
	}

	/**
	 * getRuleEvents
	 */
	getRuleEvents() {
		this.ruleEvents = [];
		this.filteredRuleEvents = [];

		if (this.panelData) {
			// Create a local Subject to control the subscription lifecycle
			const fetchDataSubject = new Subject<void>();
			this.eventsDataService.getRuleEvents$(this.accountGUID, this.organisationUID, 0, 0, this.startTime, this.endTime).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((ruleEvents) => !!ruleEvents),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (ruleEvents: any) => {
					const assetIDs: number[] = this.assets.map((x: LevelAsset) => x.AssetID);
					ruleEvents = ruleEvents.filter((x: RuleEvent) => !x.Archived); //issue #92
					if (assetIDs.length > 0) {
						this.ruleEvents = ruleEvents.filter((x: RuleEvent) => assetIDs.includes(x.AssetID));
					} else {
						this.ruleEvents = ruleEvents;
					}
					this.filteredRuleEvents = this.ruleEvents;
				},
				error: (error) => {
					if (error.status == 404) {
						this.ruleEvents = [];
						this.filteredRuleEvents = [];
					}
				}
			});
		} else {
			const newRuleEvent: RuleEvent = {
				RuleEventID: 0,
				RuleID: 0,
				RuleText: "°C > 10 for 30 Mins",
				RuleValues: "°C: 11",
				TimeStamp: new Date().getTime(),
				AssetName: "Example Asset",
				RuleName: "Example Rule",
				Acknowledged: false,
				AssetID: 0,
				Archived: false,
				AcknowledgedUser: 0,
				AcknowledgedTimestamp: 0,
				TimelineID: 0,
				Comments: "",
				CaseID: ""
			};
			this.ruleEvents = [
				newRuleEvent,
				newRuleEvent,
				newRuleEvent,
				newRuleEvent,
				newRuleEvent,
				newRuleEvent,
				newRuleEvent,
				newRuleEvent,
				newRuleEvent,
				newRuleEvent
			];
			this.filteredRuleEvents = this.ruleEvents;
		}
	}

	/**
	 * getOfflineEvents
	 * @param timeStampFrom timeStampFrom 
	 * @param timeStampTo timeStampTo 
	 */
	getOfflineEvents() {
		this.sensorEvents = [];
		this.filteredSensorEvents = [];

		if (this.panelData) {
			const fetchDataSubject = new Subject<void>();
			this.eventsDataService.getOfflineEvents$(this.accountGUID, this.organisationUID, 0, this.endTime, this.startTime).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((sensorEvents) => !!sensorEvents),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (sensorEvents: any) => {
					const assetIDs: number[] = this.assets.map((x: LevelAsset) => x.AssetID);
					if (assetIDs.length > 0) {
						this.sensorEvents = sensorEvents.filter((x: OfflineEvent) => this.checkAssetID(x, assetIDs));
					} else {
						this.sensorEvents = sensorEvents;
					}
					this.filteredSensorEvents = this.sensorEvents;
				},
				error: (error) => {
					if (error.status == 404) {
						this.sensorEvents = [];
						this.filteredSensorEvents = []
					}
				}
			});
		} else {
			const newofflineEvent: OfflineEvent = {
				OfflineEventID: 0,
				SensorID: 0,
				SensorName: "Example Sensor",
				OfflineEventText: "Example Sensor has been offline for more than 60 minutes.",
				TimeStamp: new Date().getTime(),
				Acknowledged: false,
				AcknowledgedUser: 0,
				AcknowledgedTimestamp: 0,
				Subscribers: "",
				AssetIDs: "",
				SensorEventType: 0
			};
			this.sensorEvents = [
				newofflineEvent,
				newofflineEvent,
				newofflineEvent,
				newofflineEvent,
				newofflineEvent,
				newofflineEvent,
				newofflineEvent,
				newofflineEvent,
				newofflineEvent,
				newofflineEvent
			];
			this.filteredSensorEvents = this.sensorEvents;
		}
	}

	/**
	 * getDeviationEvents
	 * @param timeStampFrom timeStampFrom 
	 * @param timeStampTo timeStampTo 
	 */
	getDeviationEvents() {
		this.deviationEvents = [];
		this.filteredDeviationEvents = [];

		if (this.panelData) {
			const fetchDataSubject = new Subject<void>();
			this.eventsDataService.getDeviationEvents$(this.accountGUID, this.organisationUID, 0, 0, this.startTime, this.endTime).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((deviationEvents) => !!deviationEvents),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (deviationEvents: any) => {
					const assetIDs: number[] = this.assets.map((x: LevelAsset) => x.AssetID);
					if (assetIDs.length > 0) {
						this.deviationEvents = deviationEvents.filter((x: DeviationEvent) => assetIDs.includes(x.AssetID));
					} else {
						this.deviationEvents = deviationEvents;
					}
					this.filteredDeviationEvents = this.deviationEvents;
				},
				error: (error) => {
					if (error.status == 404) {
						this.deviationEvents = [];
						this.filteredDeviationEvents = [];
					}
				}
			});
		} else {
			const newDeviationEvent: DeviationEvent = {
				DeviationEventID: 0,
				AssetDistributionID: 0,
				DatapointID: 0,
				AcceptableDeviation: 0,
				AssetID: 0,
				ZScore: 0,
				Sensitivity: 0,
				PercentageDeviated: 0,
				Timestamp: new Date().getTime(),
				Acknowledged: false,
				EncodedImage: "",
				AssetName: "Example Asset",
				AssetDescription: "Example Asset Description",
				Message: "",
				DPTitle: "Example Datapoint - Temperature (°C)",
				AcknowledgedUser: 0,
				AcknowledgedTimestamp: 0,
				ModelType: 0
			};
			this.deviationEvents = [
				newDeviationEvent,
				newDeviationEvent,
				newDeviationEvent,
				newDeviationEvent,
				newDeviationEvent,
				newDeviationEvent,
				newDeviationEvent,
				newDeviationEvent,
				newDeviationEvent,
				newDeviationEvent
			];
			this.filteredDeviationEvents = this.deviationEvents;
		}
	}

	/**
	 * getWebhookEvents
	 */
	getWebhookEvents() {
		this.webhookEvents = [];
		this.filteredWebhookEvents = [];

		if (this.panelData) {
			const fetchDataSubject = new Subject<void>();
			this.eventsDataService.getWebhookEvents$(this.accountGUID, this.organisationUID, 0, this.endTime, this.startTime).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((webhookEvents) => !!webhookEvents),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (webhookEvents: any) => {
					this.webhookEvents = webhookEvents;
					this.filteredWebhookEvents = this.webhookEvents;
				},
				error: (error) => {
					if (error.status == 404) {
						this.webhookEvents = [];
						this.filteredWebhookEvents = []
					}
				}
			});
		} else {
			const newWebhookEvent: WebhookEvent = {
				WebhookEventID: 0,
				WebhookID: 0,
				ClientWebhookID: "",
				WebhookUID: "",
				Description: "Example Webhook",
				WebhookType: 0,
				WebhookEventType: 0,
				TimeStamp: new Date().getTime(),
				Subscribers: "",
				WebhookEventText: "Example Webhook is Offline"
			};
			this.webhookEvents = [
				newWebhookEvent,
				newWebhookEvent,
				newWebhookEvent,
				newWebhookEvent,
				newWebhookEvent,
				newWebhookEvent,
				newWebhookEvent,
				newWebhookEvent,
				newWebhookEvent,
			];
			this.filteredWebhookEvents = this.webhookEvents;
		}
	}

	/**
	 * tabChange
	 * @param event event  
	 */
	tabChange(event: any) {
		switch (event.selectingIndex) {
			case 0:
				this.getRuleEvents();
				break;
			case 1:
				this.getOfflineEvents();
				break;
			case 2:
				this.getDeviationEvents();
				break;
			case 3:
				this.getWebhookEvents();
				break;
		}
	}

	/**
	 * checkAssetID
	 * @param sensorEvent sensorEvent  
	 * @param assetIDs assetIDs 
	 * @returns 
	 */
	checkAssetID(sensorEvent: OfflineEvent, assetIDs: number[]): boolean {
		let result: boolean = false;
		sensorEvent.AssetIDs?.split(",").map((x: string) => parseInt(x)).forEach((assetID: number) => {
			if (assetIDs.includes(assetID)) {
				result = true;
			}
		});
		return result;
	}

	/**
	 * convertEpochToShortDate
	 * @param epoch epoch 
	 * @returns 
	 */
	convertEpochToShortDate(epoch: number): string {
		return this.getShortDate(new Date(epoch));
	}

	/** getShortDate */
	getShortDate(date: Date): string {
		let result: string = "";
		result += date.getDate();
		result +=
			" " +
			this.shortMonths[date.getMonth()] +
			" " +
			date.getHours().toString().padStart(2, "0") +
			":" +
			date.getMinutes().toString().padStart(2, "0");

		return result;
	}

	/** goToRuleEvent */
	goToRuleEvent(ruleEvent: RuleEvent) {
		if (this.panelData) this.router.navigate([`./${this.accountGUID}/organisation/${this.organisationUID}/event/${ruleEvent.RuleEventID}`], { queryParams: { eventType: 2 } });
	}

	/** goToOfflineEvent */
	goToOfflineEvent(offlineEvent: OfflineEvent) {
		if (this.panelData) this.router.navigate([`./${this.accountGUID}/organisation/${this.organisationUID}/event/${offlineEvent.OfflineEventID}`], { queryParams: { eventType: 1 } });
	}

	/** goToDeviationEvent */
	goToDeviationEvent(deviationEvent: DeviationEvent) {
		if (this.panelData) this.router.navigate([`./${this.accountGUID}/organisation/${this.organisationUID}/event/${deviationEvent.DeviationEventID}`], { queryParams: { eventType: 4 } });
	}

	/** goToWebhookEvent */
	goToWebhookEvent(webhookEvent: WebhookEvent) {
		if (this.panelData) this.router.navigate([`./${this.accountGUID}/organisation/${this.organisationUID}/event/${webhookEvent.WebhookEventID}`], { queryParams: { eventType: 3 } });
	}
}
